import React, { forwardRef, useState } from 'react';
import { Button as ButtonUI } from '@/components/ui/button';
import { twJoin } from 'tailwind-merge';
import { Loader2Icon } from 'lucide-react';

const buttonCommon =
  'font-pretendardRegular font-medium transition-color inline-flex items-center justify-center rounded-md transition-transform hover:translate-y-[-2px]  focus-visible:border-gray9 focus-visible:outline-none focus-visible:ring-0  focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-20';
const buttonColorData = {
  primary:
    'text-white bg-primary border border-primary hover:bg-primary500 hover:border-primary500 active:bg-primary600 active:border-primary600',
  secondary:
    'text-primary bg-primary100 border border-primary200 hover:bg-primary200 hover:border-primary200 active:bg-primary100 active:border-primary active:text-primary',
  outline:
    'text-gray9 bg-white border border-gray1 hover:bg-gray1 hover:border-gray2 active:bg-white active:border-gray4',
  red: 'text-red5 bg-white border border-gray1 hover:bg-red0 hover:border-red5 active:bg-red1',
  ghost:
    'text-gray9 bg-white border border-white hover:bg-gray1 active:bg-white active:border-gray4',
  green:
    'text-green bg-green1 border border-green1 hover:bg-green2 hover:border-green2 active:bg-green3 active:border-green3',
};

const buttonSizeData = {
  large: 'px-4 py-2 h-[40px] text-[16px]',
  medium: 'px-4 py-2 h-[36px] text-[14px]',
  small: 'px-3 py-2 h-[32px] text-[14px]',
  xSmall: 'px-2 py-1 h-[24px] text-[12px]',
};

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  buttonSize?: keyof typeof buttonSizeData;
  buttonColor?: keyof typeof buttonColorData;
  isLoading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      buttonSize = 'large',
      buttonColor = 'primary',
      isLoading,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const [isHover, setIsHover] = useState(false);

    const isHoverButtonStyle = isHover
      ? 'transform translate-y-[-2px]'
      : 'transform translate-y-0';

    return (
      <ButtonUI
        ref={ref}
        className={twJoin(
          buttonCommon,
          buttonSizeData[buttonSize],
          buttonColorData[buttonColor],
          isHoverButtonStyle,
          className,
        )}
        onMouseOver={() => {
          setIsHover(true);
        }}
        onMouseLeave={() => {
          setIsHover(false);
        }}
        type="button"
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading && <Loader2Icon size={16} className="mr-2 animate-spin" />}{' '}
        {children}
      </ButtonUI>
    );
  },
);

export default Button;
